import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  BookText,
  Briefcase,
  Copy,
  FileText,
  Lock,
  Mail,
  MessageCircle,
  Search,
  ShoppingBag,
  X,
} from 'lucide-react';
import { api } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import { SkeletonGrid } from '../../components/Skeleton';
import { useFeature, upgradeHintFor } from '../../lib/features';
import { toast } from '../../lib/toast';

const KIND_TABS = [
  { kind: 'email', label: 'Cold emails', icon: Mail },
  { kind: 'social', label: 'Social posts', icon: MessageCircle },
  { kind: 'service-menu', label: 'Service menus', icon: Briefcase },
  { kind: 'proposal', label: 'Proposals', icon: FileText },
  { kind: 'case-study', label: 'Case studies', icon: ShoppingBag },
];

function copyToClipboard(text, what = 'Template') {
  navigator.clipboard.writeText(text);
  toast.success(`${what} copied`);
}

export default function SalesTemplatesLibraryTab() {
  const unlocked = useFeature('agencyClientWorkspaces');
  const [active, setActive] = useState('email');
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setQuery(searchInput), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const metaQuery = useQuery({
    queryKey: ['sales-templates-meta'],
    queryFn: () => api.get('/api/sales-templates/meta').then((r) => r.data),
    enabled: unlocked,
    retry: false,
  });

  if (!unlocked) {
    return (
      <div className="space-y-5">
        <PageHeader
          variant="gradient"
          icon={BookText}
          eyebrow="Agency"
          title="Sales templates library"
          description="Cold emails, social posts, service menus, proposals, and case-study skeletons curated for AEO agencies."
        />
        <div className="card border-dashed border-2 border-gray-200 bg-gray-50/60">
          <div className="section-header">
            <div className="section-title">
              <span className="section-icon">
                <Lock className="w-4 h-4" />
              </span>
              Locked
              <span className="pill bg-gray-100 text-gray-500">Agency</span>
            </div>
          </div>
          <p className="text-sm text-ink-600">
            Sales templates are part of the Agency tier — ready-to-clone cold outreach scripts,
            social posts, service-menu pages, proposal skeletons, and case-study frameworks built
            for selling AEO services.
          </p>
          <p className="text-xs text-ink-400 mt-2">
            Upgrade required: {upgradeHintFor('agencyClientWorkspaces')}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        variant="gradient"
        icon={BookText}
        eyebrow="Agency"
        title="Sales templates library"
        description="Browse, copy, and adapt the AEO sales playbook — cold outreach, social, service menus, proposals, and case studies."
      />

      <div className="card">
        {/* Sub-tab bar */}
        <div className="flex items-center gap-1 flex-wrap border-b border-gray-100 -mx-6 px-6 pb-3 mb-4 overflow-x-auto scroll-soft">
          {KIND_TABS.map((tab) => {
            const Icon = tab.icon;
            const count = metaQuery.data?.counts?.[tab.kind];
            return (
              <button
                key={tab.kind}
                type="button"
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg whitespace-nowrap transition ${
                  active === tab.kind
                    ? 'bg-brand-50 text-brand-700 font-medium'
                    : 'text-ink-600 hover:bg-gray-50'
                }`}
                onClick={() => setActive(tab.kind)}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {typeof count === 'number' && (
                  <span className="text-xs text-ink-400 font-normal">({count})</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10 max-w-xs"
              placeholder="Search…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          {query && (
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => {
                setSearchInput('');
                setQuery('');
              }}
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>

        <TemplateList kind={active} query={query} />
      </div>
    </div>
  );
}

function TemplateList({ kind, query }) {
  const params = new URLSearchParams();
  params.set('kind', kind);
  if (query) params.set('q', query);

  const queryResult = useQuery({
    queryKey: ['sales-templates', kind, query],
    queryFn: () => api.get(`/api/sales-templates?${params}`).then((r) => r.data),
    placeholderData: keepPreviousData,
  });

  const rows = queryResult.data?.rows || [];

  if (queryResult.isLoading) return <SkeletonGrid count={3} />;
  if (!rows.length) {
    return (
      <EmptyState
        icon={BookText}
        title="No templates match."
        body={query ? 'Try clearing the search.' : 'Content team adds new templates regularly.'}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-ink-500">{rows.length} templates</div>
      {rows.map((row) => (
        <TemplateCard key={row._id} row={row} />
      ))}
    </div>
  );
}

function TemplateCard({ row }) {
  const [open, setOpen] = useState(false);
  const onCopy = () => {
    const text = flattenTemplate(row);
    copyToClipboard(text, row.name);
  };
  return (
    <div className="border border-gray-100 rounded-xl p-4 hover:border-brand-200 transition">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-ink-900">{row.name}</div>
          {row.description && (
            <p className="text-sm text-ink-600 mt-1 leading-relaxed">{row.description}</p>
          )}
          {row.bestFor && (
            <p className="text-xs text-ink-500 mt-1">
              <span className="font-semibold">Best for:</span> {row.bestFor}
            </p>
          )}
        </div>
        <button type="button" className="btn-ghost text-xs flex-shrink-0" onClick={onCopy}>
          <Copy className="w-3 h-3" /> Copy
        </button>
      </div>

      <button
        type="button"
        className="text-xs text-brand-700 hover:underline"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide template' : 'Show template'}
      </button>

      {open && (
        <pre className="mt-3 text-xs whitespace-pre-wrap bg-gray-50 rounded-lg p-3 text-ink-700 break-words">
          {flattenTemplate(row)}
        </pre>
      )}
    </div>
  );
}

function flattenTemplate(row) {
  const p = row.payload || {};
  if (row.kind === 'email') {
    const vars =
      p.variables?.length > 0 ? `\n\nVariables to fill in: ${p.variables.join(', ')}` : '';
    return `Subject: ${p.subject || ''}\n\n${p.body || ''}${vars}`;
  }
  if (row.kind === 'social') {
    const tags = p.hashtags?.length ? `\n\n${p.hashtags.map((h) => `#${h}`).join(' ')}` : '';
    return `${p.body || ''}${tags}`;
  }
  if (row.kind === 'service-menu') {
    return (p.packages || [])
      .map(
        (pk) =>
          `## ${pk.name} — ${pk.price}\n${(pk.includes || []).map((i) => `- ${i}`).join('\n')}`,
      )
      .join('\n\n');
  }
  if (row.kind === 'proposal') {
    return (p.sections || [])
      .map((s) => `## ${s.heading}\n${s.body || ''}`)
      .join('\n\n');
  }
  if (row.kind === 'case-study') {
    const metrics = p.metrics?.length
      ? `\n\n## Metrics\n${p.metrics.map((m) => `- ${m}`).join('\n')}`
      : '';
    return `## Problem\n${p.problem || ''}\n\n## Approach\n${p.approach || ''}\n\n## Outcome\n${p.outcome || ''}${metrics}`;
  }
  return JSON.stringify(p, null, 2);
}
